import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollContext } from './ScrollContainer';

/* --------------------------------------------------------------------------
 * ACHIEVEMENT WALL — TWO-BATCH SCROLL STORYBOARD
 *
 *  0.00 - 0.15 : Headline enters from bottom into center stage and pins
 *  0.15 - 0.20 : Headline holds center
 *  0.20 - 0.40 : Batch 1 (cards 01-07) enter from bottom with motion & stagger
 *  0.40 - 0.57 : Batch 1 spreads radially outward and clears the stage
 *  0.58 - 0.79 : Batch 2 rises from the bottom into the cleared slots
 *  0.80 - 0.85 : Batch 2 holds on stage
 *  0.85 - 1.00 : Batch 2 spreads outward as the stage releases
 * -------------------------------------------------------------------------- */
const SCROLL_TIMING = {
  titleEnterStart: 0.00,
  titleEnterEnd: 0.12,
  batch1EnterStart: 0.16,
  batch1CardDuration: 0.14,
  batch1CardStagger: 0.015,
  batch1ExitStart: 0.40,
  batch1ExitDuration: 0.12,
  batch1ExitStagger: 0.008,
  batch2EnterStart: 0.58,
  batch2CardDuration: 0.13,
  batch2CardStagger: 0.013,
  stageExitStart: 0.84,
  stageExitDuration: 0.12,
  stageExitStagger: 0.006,
  maxSpeed: 0.45, // max progress units per second (independent of scroll speed)
  lerpFactor: 12.0, // smooth follow factor for normal scroll
};

const ITEMS_PER_BATCH = 7;
const TOTAL_IMAGES = ITEMS_PER_BATCH * 2;

const ACHIEVEMENT_IMAGES = [
  '/assets/achievements/dainikbhaskar_newspaper.webp',
  '/assets/achievements/nit_raipur_grp.webp',
  '/assets/achievements/quasar_3.0_grp.webp',
  '/assets/achievements/recursion6.0_grp.webp',
  '/assets/achievements/sih_grp.webp',
  '/assets/achievements/sih_solo.webp',
  '/assets/achievements/sih_solo1.webp',
  '/assets/achievements/sunhacks_grp.webp',
  '/assets/achievements/sunhacksgrp.webp',
  '/assets/achievements/webathon_grp.webp',
];

/* --------------------------------------------------------------------------
 * SEPARATE CONFIGURATION FOR ALL 14 ACHIEVEMENT CARDS
 *
 *  x     : Desktop X position from center (-50 = far left, +50 = far right) in vw
 *  y     : Desktop Y position from center (-40 = top, +40 = bottom) in vh
 *  w     : Desktop Card width in vw
 *  scale : Desktop Scale multiplier (1.0 = normal, 0.8 = 80%, 1.2 = 120%)
 *  rotate: Organic card tilt angle in degrees (-3.0 to +3.0)
 *  ratio : Aspect ratio ('3 / 4', '4 / 3', '16 / 10', '1 / 1')
 *  mx    : Mobile X position from center in vw
 *  my    : Mobile Y position from center in vh
 *  mw    : Mobile Card width in vw
 *  mScale: Mobile Scale multiplier (optional, defaults to scale)
 * -------------------------------------------------------------------------- */
const CARD_CONFIGS = [
  // --- BATCH 1 (CARDS 01 TO 07) ---
  /* Card 01 (Newspaper Clipping) */ { x: -37.9, y: -20.8, w: 20.7, scale: 1.0, rotate: -0.4, ratio: '0.76',  mx: -28, my: -33, mw: 40, mScale: 1.0 },
  /* Card 02 (Nit Raipur Group)   */ { x: -12.5, y: -27.7, w: 20.2, scale: 1.0, rotate: 0,    ratio: '1.42',  mx: 25,  my: -40, mw: 44, mScale: 1.0 },
  /* Card 03 (Quasar 3.0 Stage)   */ { x: 11.4,  y: -31.0, w: 20.2, scale: 1.0, rotate: 0,    ratio: '1.125', mx: 25,  my: -20, mw: 40, mScale: 1.0 },
  /* Card 04 (Recursion Group)    */ { x: 34.2,  y: -7.8,  w: 19.5, scale: 1.0, rotate: 0.4,  ratio: '0.785', mx: -29, my: -6,  mw: 34, mScale: 1.0 },
  /* Card 05 (SIH Group)          */ { x: -32.9, y: 26.7,  w: 24.3, scale: 1.0, rotate: 0,    ratio: '1.54',  mx: 25,  my: -1,  mw: 42, mScale: 1.0 },
  /* Card 06 (SIH Finale Stage)   */ { x: -5.6,  y: 30.2,  w: 20.9, scale: 1.0, rotate: 0,    ratio: '1.355', mx: -25, my: 32,  mw: 44, mScale: 1.0 },
  /* Card 07 (SIH Trophy Solo)    */ { x: 19.3,  y: 28.9,  w: 17.0, scale: 1.0, rotate: 0,    ratio: '0.905', mx: 25,  my: 34,  mw: 36, mScale: 1.0 },

  // --- BATCH 2 (CARDS 08 TO 14) ---
  /* Card 08 (Sunhacks Group)     */ { x: -37.9, y: -20.8, w: 20.7, scale: 1.0, rotate: -0.4, ratio: '0.76',  mx: -28, my: -33, mw: 40, mScale: 1.0 },
  /* Card 09 (Sunhacks Grp 2)     */ { x: -12.5, y: -27.7, w: 20.2, scale: 1.0, rotate: 0,    ratio: '1.42',  mx: 25,  my: -40, mw: 44, mScale: 1.0 },
  /* Card 10 (Webathon Group)     */ { x: 11.4,  y: -31.0, w: 20.2, scale: 1.0, rotate: 0,    ratio: '1.125', mx: 25,  my: -20, mw: 40, mScale: 1.0 },
  /* Card 11 (Newspaper Alt)      */ { x: 34.2,  y: -7.8,  w: 19.5, scale: 1.0, rotate: 0.4,  ratio: '0.785', mx: -29, my: -6,  mw: 34, mScale: 1.0 },
  /* Card 12 (Quasar Group Alt)   */ { x: -32.9, y: 26.7,  w: 24.3, scale: 1.0, rotate: 0,    ratio: '1.54',  mx: 25,  my: -1,  mw: 42, mScale: 1.0 },
  /* Card 13 (NIT Raipur Alt)     */ { x: -5.6,  y: 30.2,  w: 20.9, scale: 1.0, rotate: 0,    ratio: '1.355', mx: -25, my: 32,  mw: 44, mScale: 1.0 },
  /* Card 14 (SIH Solo Alt)       */ { x: 19.3,  y: 28.9,  w: 17.0, scale: 1.0, rotate: 0,    ratio: '0.905', mx: 25,  my: 34,  mw: 36, mScale: 1.0 },
];

const RANDOM_CARD_COLORS = [
  '#1e293b', '#14532d', '#7c2d12', '#581c87',
  '#831843', '#064e3b', '#1e1b4b', '#451a03',
  '#312e81', '#701a75', '#164e63', '#365314',
  '#0f172a', '#2e1065'
];

const WallSection = styled.section`
  position: relative;
  width: 100%;
  height: 500vh;
  background-color: #bebebe;

  @media (max-width: 700px) {
    height: 560svh;
  }

  @media (prefers-reduced-motion: reduce) {
    height: 100vh;
    height: 100dvh;
  }
`;

const StickyStage = styled.div`
  position: sticky;
  top: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  isolation: isolate;
`;

const Headline = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 10;
  width: min(74vw, 58rem);
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
  will-change: transform, opacity;

  @media (max-width: 700px) {
    left: 50%;
    top: 63%;
    width: calc(100% - 2.5rem);
  }
`;

const Title = styled.h2`
  margin: 0;
  font-family: var(--font-portfolio-display);
  font-size: clamp(4.75rem, 7.5vw, 7.25rem);
  font-weight: 800;
  line-height: 0.72;
  letter-spacing: -0.085em;
  color: #751628;

  .title-script {
    display: block;
    width: max-content;
    margin: 0 auto -0.04em;
    font-family: var(--font-portfolio-serif);
    font-size: 0.585em;
    font-weight: 500;
    font-style: italic;
    line-height: 0.9;
    letter-spacing: -0.045em;
    transform: translateX(-26%);
  }

  .title-display {
    position: relative;
    display: inline-block;
    text-transform: lowercase;
  }

  .title-underline {
    position: absolute;
    left: 8%;
    bottom: -0.23em;
    width: 87%;
    height: 0.23em;
    overflow: visible;
    color: inherit;
  }

  .title-underline path {
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    vector-effect: non-scaling-stroke;
  }

  @media (max-width: 700px) {
    font-size: clamp(3rem, 13vw, 4.5rem);
    line-height: 0.78;
    letter-spacing: -0.06em;

    .title-script {
      font-size: 0.52em;
      transform: none;
    }

    .title-underline {
      bottom: -0.20em;
    }
  }
`;

const Gallery = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
`;

const PhotoButton = styled.button`
  appearance: none;
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: var(--photo-z);
  width: clamp(7rem, var(--desktop-width), 26rem);
  aspect-ratio: var(--photo-ratio);
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  pointer-events: auto;
  transform: translate(calc(-50% + var(--desktop-x)), calc(-50% + var(--desktop-y))) rotate(var(--photo-rotate, 0deg));
  will-change: transform, opacity;

  &:focus-visible {
    outline: 2px solid #bebebe;
    outline-offset: 5px;
    z-index: 40 !important;
  }

  &[aria-expanded='true'] {
    z-index: 40 !important;
  }

  @media (hover: hover) {
    &:hover {
      z-index: 40 !important;
    }

    &:hover > div {
      transform: scale(1.045) translateY(-5px);
      box-shadow:
        0 26px 50px -10px rgba(0, 0, 0, 0.65),
        0 10px 24px -4px rgba(0, 0, 0, 0.45);
      border-color: rgba(255, 255, 255, 0.6);
    }
  }

  &[aria-expanded='true'] > div {
    transform: scale(1.045) translateY(-5px);
    box-shadow:
      0 26px 50px -10px rgba(0, 0, 0, 0.65),
      0 10px 24px -4px rgba(0, 0, 0, 0.45);
    border-color: rgba(255, 255, 255, 0.6);
  }

  &:active > div {
    transform: scale(0.985);
  }

  @media (max-width: 700px) {
    width: clamp(5rem, var(--mobile-width), 13rem);
    transform: translate(calc(-50% + var(--mobile-x)), calc(-50% + var(--mobile-y))) rotate(var(--photo-rotate, 0deg));
  }

  @media (prefers-reduced-motion: reduce) {
    & > div {
      transition: none;
    }
  }
`;

const PhotoSurface = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border: 1.5px solid rgba(255, 255, 255, 0.22);
  border-radius: 10px;
  background-color: var(--card-bg, #171113);
  box-shadow:
    0 16px 36px -8px rgba(0, 0, 0, 0.5),
    0 4px 12px -2px rgba(0, 0, 0, 0.3);
  transition:
    transform 220ms cubic-bezier(0.23, 1, 0.32, 1),
    border-color 220ms cubic-bezier(0.23, 1, 0.32, 1),
    box-shadow 220ms cubic-bezier(0.23, 1, 0.32, 1);
`;

const PhotoImage = styled.img`
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  background: transparent;
`;

const Achievements = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const sectionRef = useRef(null);
  const headlineRef = useRef(null);
  const galleryRef = useRef(null);
  const { containerRef: scrollContainerRef } = useContext(ScrollContext);

  useEffect(() => {
    const section = sectionRef.current;
    const gallery = galleryRef.current;
    if (!section || !gallery) return undefined;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return undefined;

    const scroller = scrollContainerRef?.current || window;
    const cards = gsap.utils.toArray(gallery.querySelectorAll('[data-achievement-card]'));
    if (cards.length !== TOTAL_IMAGES) return undefined;

    const firstBatch = cards.slice(0, ITEMS_PER_BATCH);
    const secondBatch = cards.slice(ITEMS_PER_BATCH);

    let timeline;

    const getPosition = (index, axis) => {
      const layout = CARD_CONFIGS[index % CARD_CONFIGS.length];
      const isMobile = window.innerWidth <= 700;
      const value = isMobile
        ? layout[axis === 'x' ? 'mx' : 'my']
        : layout[axis];
      const viewport = axis === 'x' ? window.innerWidth : window.innerHeight;
      return (value / 100) * viewport;
    };

    const getCardScale = (index) => {
      const config = CARD_CONFIGS[index % CARD_CONFIGS.length];
      const isMobile = window.innerWidth <= 700;
      if (!config) return 1;
      if (!isMobile) return config.scale !== undefined ? config.scale : 1;

      const configuredScale = config.mScale !== undefined
        ? config.mScale
        : (config.scale !== undefined ? config.scale : 1);
      const shortScreenScale = Math.max(0.86, Math.min(1, window.innerHeight / 720));

      return configuredScale * shortScreenScale;
    };

    const getCardRotate = (index) => {
      const config = CARD_CONFIGS[index % CARD_CONFIGS.length];
      return config?.rotate || 0;
    };

    const getRadialPosition = (index, axis) => {
      const slotX = getPosition(index, 'x');
      const slotY = getPosition(index, 'y');
      const slotDistance = Math.max(1, Math.hypot(slotX, slotY));
      const exitDistance = Math.hypot(window.innerWidth, window.innerHeight) * 0.58;
      const direction = axis === 'x' ? slotX / slotDistance : slotY / slotDistance;
      const slotPosition = axis === 'x' ? slotX : slotY;

      return slotPosition + direction * exitDistance;
    };

    const getRadialRotation = (index) => {
      const baseRotate = getCardRotate(index);
      const slotX = getPosition(index, 'x');
      const horizontalThreshold = window.innerWidth * 0.04;

      if (Math.abs(slotX) < horizontalThreshold) return baseRotate;
      return baseRotate + Math.sign(slotX) * (6 + (index % 3) * 1.5);
    };

    const context = gsap.context(() => {
      // 1. Set initial section background color to dark #171113
      gsap.set(sectionRef.current, {
        backgroundColor: '#000000ff',
      });

      // 2. Headline enters from bottom into screen center like normal section scroll
      gsap.set(headlineRef.current, {
        autoAlpha: 0,
        yPercent: 120,
        scale: 0.95,
      });

      // 3. Both batches start below the stage and rise into their wall slots.
      firstBatch.forEach((card, index) => {
        gsap.set(card, {
          autoAlpha: 0,
          xPercent: -50,
          yPercent: -50,
          x: () => getPosition(index, 'x'),
          y: () => window.innerHeight * 0.85,
          scale: () => getCardScale(index) * 0.75,
          rotation: () => getCardRotate(index) + (index % 2 === 0 ? -3 : 3),
        });
      });

      secondBatch.forEach((card, index) => {
        const globalIdx = index + ITEMS_PER_BATCH;

        gsap.set(card, {
          autoAlpha: 0,
          xPercent: -50,
          yPercent: -50,
          x: () => getPosition(globalIdx, 'x'),
          y: () => window.innerHeight * 0.85,
          scale: () => getCardScale(globalIdx) * 0.75,
          rotation: () => getCardRotate(globalIdx) + (index % 2 === 0 ? -3 : 3),
        });
      });

      timeline = gsap.timeline({ paused: true, defaults: { overwrite: 'auto' } });

      // PHASE 1: Smooth Background Blend (Dark #171113 -> Bright Silver #bebebe) & Headline Entry (0.00 - 0.16)
      timeline.to(sectionRef.current, {
        backgroundColor: '#bebebe',
        duration: SCROLL_TIMING.batch1EnterStart - SCROLL_TIMING.titleEnterStart,
        ease: 'power2.inOut',
      }, SCROLL_TIMING.titleEnterStart);

      timeline.to(headlineRef.current, {
        autoAlpha: 1,
        yPercent: -50,
        scale: 1,
        duration: SCROLL_TIMING.titleEnterEnd - SCROLL_TIMING.titleEnterStart,
        ease: 'power3.out',
      }, SCROLL_TIMING.titleEnterStart);

      // PHASE 2: Batch 1 (cards 01-07) rise from bottom & settle in wall positions (0.16 - 0.39)
      firstBatch.forEach((card, index) => {
        const staggerOffset = index * SCROLL_TIMING.batch1CardStagger;

        timeline.to(card, {
          autoAlpha: 1,
          x: () => getPosition(index, 'x'),
          y: () => getPosition(index, 'y'),
          scale: () => getCardScale(index),
          rotation: () => getCardRotate(index),
          duration: SCROLL_TIMING.batch1CardDuration,
          ease: 'power3.out',
        }, SCROLL_TIMING.batch1EnterStart + staggerOffset);
      });

      // PHASE 3: Batch 1 fans outward from the center along its slot directions.
      firstBatch.forEach((card, index) => {
        const staggerOffset = index * SCROLL_TIMING.batch1ExitStagger;

        timeline.to(card, {
          autoAlpha: 0,
          x: () => getRadialPosition(index, 'x'),
          y: () => getRadialPosition(index, 'y'),
          scale: () => getCardScale(index) * 0.82,
          rotation: () => getRadialRotation(index),
          duration: SCROLL_TIMING.batch1ExitDuration,
          ease: 'power3.in',
        }, SCROLL_TIMING.batch1ExitStart + staggerOffset);
      });

      // PHASE 4: Batch 2 rises from the bottom after Batch 1 clears.
      secondBatch.forEach((card, index) => {
        const globalIdx = index + ITEMS_PER_BATCH;
        const staggerOffset = index * SCROLL_TIMING.batch2CardStagger;

        timeline.to(card, {
          autoAlpha: 1,
          x: () => getPosition(globalIdx, 'x'),
          y: () => getPosition(globalIdx, 'y'),
          scale: () => getCardScale(globalIdx),
          rotation: () => getCardRotate(globalIdx),
          duration: SCROLL_TIMING.batch2CardDuration,
          ease: 'power3.out',
        }, SCROLL_TIMING.batch2EnterStart + staggerOffset);
      });

      // PHASE 5: Release the stage while Batch 2 fans back out radially.
      timeline.to(sectionRef.current, {
        backgroundColor: '#000000ff',
        duration: 0.14,
        ease: 'power2.inOut',
      }, SCROLL_TIMING.stageExitStart);

      secondBatch.forEach((card, index) => {
        const globalIdx = index + ITEMS_PER_BATCH;
        const staggerOffset = index * SCROLL_TIMING.stageExitStagger;

        timeline.to(card, {
          autoAlpha: 0,
          x: () => getRadialPosition(globalIdx, 'x'),
          y: () => getRadialPosition(globalIdx, 'y'),
          scale: () => getCardScale(globalIdx) * 0.82,
          rotation: () => getRadialRotation(globalIdx),
          duration: SCROLL_TIMING.stageExitDuration,
          ease: 'power3.in',
        }, SCROLL_TIMING.stageExitStart + staggerOffset);
      });

      timeline.to(headlineRef.current, {
        autoAlpha: 0,
        yPercent: -120,
        scale: 0.94,
        duration: SCROLL_TIMING.stageExitDuration,
        ease: 'power2.inOut',
      }, SCROLL_TIMING.stageExitStart);

    }, section);

    let targetProgress = 0;
    let currentProgress = 0;

    const unlockProgressDown = SCROLL_TIMING.stageExitStart; // 0.84 - Batch 2 cards complete
    const unlockProgressUp = SCROLL_TIMING.titleEnterEnd;    // 0.12 - Stage start

    const isMobile = window.innerWidth <= 700;
    const effectiveMaxSpeed = isMobile ? 0.60 : SCROLL_TIMING.maxSpeed;
    const effectiveLerpFactor = isMobile ? 14.0 : SCROLL_TIMING.lerpFactor;

    const getSectionProgress = () => {
      const isWindow = scroller === window;
      const scrollTop = isWindow ? window.scrollY : scroller.scrollTop;
      const viewportHeight = isWindow ? window.innerHeight : scroller.clientHeight;
      const sectionTop = isWindow
        ? scrollTop + section.getBoundingClientRect().top
        : section.offsetTop;
      const scrollDistance = Math.max(1, section.offsetHeight - viewportHeight);
      return gsap.utils.clamp(0, 1, (scrollTop - sectionTop) / scrollDistance);
    };

    targetProgress = getSectionProgress();
    currentProgress = targetProgress;
    timeline.progress(currentProgress);

    const onTick = (time, deltaTime) => {
      const dt = Math.min(deltaTime / 1000, 0.05);

      const isWindow = scroller === window;
      let scrollTop = isWindow ? window.scrollY : scroller.scrollTop;
      const viewportHeight = isWindow ? window.innerHeight : scroller.clientHeight;
      const sectionTop = isWindow
        ? scrollTop + section.getBoundingClientRect().top
        : section.offsetTop;
      const scrollDistance = Math.max(1, section.offsetHeight - viewportHeight);
      const maxScrollTop = sectionTop + scrollDistance;

      // Lock section scroll down if Batch 2 has not finished animating yet
      if (scrollTop >= maxScrollTop - 2 && currentProgress < unlockProgressDown) {
        scrollTop = maxScrollTop;
        if (isWindow) {
          window.scrollTo(window.scrollX, maxScrollTop);
        } else {
          scroller.scrollTop = maxScrollTop;
        }
      } else if (scrollTop <= sectionTop + 2 && currentProgress > unlockProgressUp && targetProgress < unlockProgressUp) {
        scrollTop = sectionTop;
        if (isWindow) {
          window.scrollTo(window.scrollX, sectionTop);
        } else {
          scroller.scrollTop = sectionTop;
        }
      }

      targetProgress = gsap.utils.clamp(0, 1, (scrollTop - sectionTop) / scrollDistance);

      const diff = targetProgress - currentProgress;
      if (Math.abs(diff) < 0.0001) {
        if (currentProgress !== targetProgress) {
          currentProgress = targetProgress;
          timeline.progress(currentProgress);
        }
        return;
      }

      // Smooth lerp for normal scroll, capped at effectiveMaxSpeed for fast scroll
      let step = diff * effectiveLerpFactor * dt;
      const maxStep = effectiveMaxSpeed * dt;

      if (Math.abs(step) > maxStep) {
        step = Math.sign(step) * maxStep;
      }

      currentProgress += step;
      timeline.progress(currentProgress);
    };

    gsap.ticker.add(onTick);

    const scrollTarget = scroller === window ? window : scroller;

    // Prevent mobile touch momentum from bypassing scroll lock
    const handleTouchMove = () => {
      const isWindow = scroller === window;
      const scrollTop = isWindow ? window.scrollY : scroller.scrollTop;
      const viewportHeight = isWindow ? window.innerHeight : scroller.clientHeight;
      const sectionTop = isWindow
        ? scrollTop + section.getBoundingClientRect().top
        : section.offsetTop;
      const maxScrollTop = sectionTop + (section.offsetHeight - viewportHeight);

      if (scrollTop >= maxScrollTop - 5 && currentProgress < unlockProgressDown) {
        if (!isWindow && scroller.scrollTop > maxScrollTop) {
          scroller.scrollTop = maxScrollTop;
        }
      }
    };

    scrollTarget.addEventListener('touchmove', handleTouchMove, { passive: true });

    const handleResize = () => {
      timeline.invalidate();
      targetProgress = getSectionProgress();
      currentProgress = targetProgress;
      timeline.progress(currentProgress);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      gsap.ticker.remove(onTick);
      scrollTarget.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('resize', handleResize);
      context.revert();
    };
  }, [scrollContainerRef]);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setActiveIndex(null);
      event.currentTarget.blur();
    }
  };

  return (
    <WallSection ref={sectionRef} id="achievements" aria-labelledby="achievements-title">
      <StickyStage>
        <Headline ref={headlineRef}>
          <Title id="achievements-title">
            <span className="title-script">made some</span>
            <span className="title-display">
              noise.
              <svg
                className="title-underline"
                viewBox="0 0 420 30"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path d="M 3 21 C 112 15, 260 10, 417 4" strokeWidth="1.7" />
                <path d="M 78 29 C 164 20, 253 14, 342 8" strokeWidth="1.35" />
              </svg>
            </span>
          </Title>
        </Headline>

        <Gallery ref={galleryRef} aria-label="Achievement photo wall">
          {Array.from({ length: TOTAL_IMAGES }, (_, index) => {
            const config = CARD_CONFIGS[index % CARD_CONFIGS.length];
            const imagePath = ACHIEVEMENT_IMAGES[index % ACHIEVEMENT_IMAGES.length];
            const imageNumber = String(index + 1).padStart(2, '0');
            const isActive = activeIndex === index;

            return (
              <PhotoButton
                key={imageNumber}
                type="button"
                data-achievement-card
                data-batch={Math.floor(index / ITEMS_PER_BATCH)}
                aria-expanded={isActive}
                aria-label={`Achievement photo ${index + 1}`}
                onClick={() => setActiveIndex(isActive ? null : index)}
                onKeyDown={handleKeyDown}
                style={{
                  '--desktop-width': `${config.w}vw`,
                  '--desktop-x': `${config.x}vw`,
                  '--desktop-y': `${config.y}vh`,
                  '--mobile-width': `${config.mw}vw`,
                  '--mobile-x': `${config.mx}vw`,
                  '--mobile-y': `${config.my}vh`,
                  '--photo-ratio': config.ratio,
                  '--photo-rotate': `${config.rotate || 0}deg`,
                  '--photo-z': (index % 4) + 1,
                  '--surface-x': `${28 + (index % 4) * 14}%`,
                  '--surface-y': `${30 + (index % 3) * 18}%`,
                  '--card-bg': RANDOM_CARD_COLORS[index % RANDOM_CARD_COLORS.length],
                }}
              >
                <PhotoSurface>
                  <PhotoImage
                    src={imagePath}
                    alt={`Achievement photo ${index + 1}`}
                    width="720"
                    height="540"
                    loading={index < ITEMS_PER_BATCH ? 'eager' : 'lazy'}
                    fetchpriority={index < ITEMS_PER_BATCH ? 'high' : 'auto'}
                    decoding="async"
                  />
                </PhotoSurface>
              </PhotoButton>
            );
          })}
        </Gallery>
      </StickyStage>
    </WallSection>
  );
};

export default Achievements;
